import React, { useLayoutEffect } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import { Indicator } from 'design-system/Indicator';
import { SettingsItem, SettingsItemSwitch } from 'design-system/SettingsItem';
import { SettingsSection } from 'design-system/SettingsSection';
import { AppView } from 'design-system/AppView';
import { rem, SCREEN_BOTTOM_PADDING } from 'design-system/values';
import * as Icons from 'design-system/icons';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import Modal from 'utils/packages/Modal';
import { useScrambleMode } from 'hooks';
import { useSmartRulesNavigation } from 'hooks/useSmartRulesNavigation';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { setDeviceSettingsKey } from 'actions/deviceSettings';
import { selectFeatureFlag } from 'reducers/selectors';
import { selectHasLiveExportFeature } from 'features/export-data-live/selectors';
import { selectHasPreApprovedTransfersFeature } from 'features/pre-approved-transfers/selectors';
import { showExportDataMethodModal } from 'features/export-data/ExportDataMethodModal';
import { useMarginBottom } from 'hooks/useMarginBottom';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { SecuritySettingsSection } from './SecuritySettingsSection';
import { DeleteAccountSettingsSection } from './DeleteAccountSettingsSection';
import { isWeb, isDesktop } from '../../../constants';
const strings = {
    headerTitle: 'Account',
    personalDetails: {
        title: 'Personal Details',
    },
    // scramble mode
    privacyMode: {
        title: 'Privacy Mode',
        description: 'Hide your balances and transactions',
    },
    dataExport: {
        title: 'Export Your Data',
    },
    fraudDetection: {
        title: 'Fraud Detection',
        description: 'Monitor your emails and phone numbers on the dark web',
    },
    smartRules: {
        title: 'Smart Rules',
    },
    web: {
        title: 'Emma for Web',
    },
    restorePurchases: {
        title: 'Restore in-app purchases',
    },
    bankLogins: {
        title: 'Bank connections',
    },
    privacyModeModal: {
        title: 'Balances and transactions are now hidden',
        description: 'Flip your device to hide/show your balance or use the switcher in Account setting screen.',
        descriptionWeb: 'Click the banner at the top to show your balance or use the switcher in Account settings screen.',
        dontShowAgain: 'Don’t show again',
        ok: 'OK',
    },
    flipToScramble: {
        title: 'Flip to enable privacy mode',
    },
};
export const AccountSettingsList = withAnimatedNavigationHeader(({ navigation, onScroll }) => {
    const dispatch = useAppDispatch();
    const userId = useAppSelector((state) => state.user.user.id);
    // if true, don’t show the modal after enabling scramble/privacy mode
    const hideScrambleModal = useAppSelector((state) => state.deviceSettings.hideScrambleModal);
    const hasAccelerometerSupport = useAppSelector((state) => state.deviceSettings.hasAccelerometerSupport);
    const flipToScramble = useAppSelector((state) => state.deviceSettings.flipToScramble);
    const hasLiveExportFeature = useAppSelector(selectHasLiveExportFeature);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => (<HelpScreenButton articlesKey={HELP_ARTICLES_KEY.SETTINGS}/>),
        });
    }, []);
    const { onPressSmartRules } = useSmartRulesNavigation();
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const marginBottom = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    const onExportYourData = hasLiveExportFeature
        ? () => showExportDataMethodModal()
        : () => navigation.navigateDeprecated('ExportData');
    const onPreApprovedTransfers = () => {
        navigation.navigateDeprecated('PreApprovedTransfers');
    };
    const hasPreApprovedTransfersFeature = useAppSelector(selectHasPreApprovedTransfersFeature);
    const { setScrambled, scrambled } = useScrambleMode();
    const handleScrambledChange = (value) => {
        setScrambled(value);
        if (value && !hideScrambleModal) {
            Modal.showConfirmation({
                title: strings.privacyModeModal.title,
                subtitle: isWeb
                    ? strings.privacyModeModal.descriptionWeb
                    : strings.privacyModeModal.description,
                confirmButtonTitle: strings.privacyModeModal.dontShowAgain,
                cancelButtonTitle: strings.privacyModeModal.ok,
                onConfirm: () => {
                    dispatch(setDeviceSettingsKey('hideScrambleModal', true));
                    Modal.hide();
                }, // Don’t show again button
                onPressCancel: () => Modal.hide(), // OK button
            });
        }
    };
    const webQRCodeScanner = useAppSelector((store) => selectFeatureFlag(store, 'web_qr_code_scanner_in_settings').value);
    if (userId === -1) {
        return (<AppView type="secondary" withFrame={false}>
          <Indicator dark/>
        </AppView>);
    }
    return (<AppView withFrame={false}>
        <ScrollView onScroll={onScroll} contentContainerStyle={marginBottom} scrollEventThrottle={16}>
          <SettingsSection noMarginTop style={styles.marginTop}>
            <SettingsItem title={strings.personalDetails.title} onPress={() => navigation.navigateDeprecated('PersonalDetails')} icon={<Icons.IconProfile />}/>
            <SecuritySettingsSection />
            {!isWeb && webQRCodeScanner ? (<SettingsItem title={strings.web.title} onPress={() => {
                navigation.navigateDeprecated('WebQRCodeScanner');
            }} icon={<Icons.IconScan alt colorKey="brand"/>}/>) : null}
          </SettingsSection>

          <SettingsSection noMarginTop style={styles.marginTop}>
            <SettingsItemSwitch value={scrambled} title={strings.privacyMode.title} description={strings.privacyMode.description} onValueChange={handleScrambledChange} icon={<Icons.IconBoldEye />}/>
            {!isDesktop && hasAccelerometerSupport ? (<SettingsItemSwitch value={flipToScramble} title={strings.flipToScramble.title} onValueChange={(value) => dispatch(setDeviceSettingsKey('flipToScramble', value))} icon={<Icons.IconDoubleRotateBold gradientKey="peach"/>}/>) : null}
          </SettingsSection>

          <SettingsSection noMarginTop style={styles.marginTop}>
            <SettingsItem title={strings.dataExport.title} onPress={onExportYourData} icon={<Icons.IconPaperDownload gradientKey="darkPurple"/>}/>
            {!isWeb && (<>
                <SettingsItem title={strings.smartRules.title} onPress={() => onPressSmartRules(false)} icon={<Icons.IconSmartRule />}/>

                {hasPreApprovedTransfersFeature ? (<SettingsItem title={'Pre-approved transfers'} onPress={onPreApprovedTransfers} icon={<Icons.IconMoneyCheck gradientKey="darkPink"/>}/>) : null}
              </>)}
            <SettingsItem title={strings.bankLogins.title} onPress={() => navigation.navigate('Logins')} icon={<Icons.IconBank gradientKey="lightPurple"/>}/>
          </SettingsSection>

          <DeleteAccountSettingsSection />
        </ScrollView>
      </AppView>);
}, ({ colors }) => ({
    title: strings.headerTitle,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    marginTop: {
        marginTop: rem(16),
    },
});
